import { surfaceByKey, type ActivitySurface } from './activityCatalog'
import type { DiscordActivityContext } from './discordSdk'

export interface LoadedActivityLaunchContext {
  surface: ActivitySurface
  sourceCommand: string | null
  sourceSubcommand: string | null
  fallback: boolean
}

const apiBase = import.meta.env.VITE_MATCHROOM_API_BASE as string | undefined

export async function loadActivityLaunchContext(
  context: DiscordActivityContext
): Promise<LoadedActivityLaunchContext | null> {
  if (!context.accessToken) return null
  const url = buildLaunchContextUrl(apiBase, context)
  if (!url) return null

  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), 2500)

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      cache: 'no-store',
      credentials: 'omit',
      headers: {
        accept: 'application/json',
        authorization: `Bearer ${context.accessToken}`
      }
    })
    if (!response.ok) return null
    return normalizeLaunchContext(await response.json())
  } catch {
    return null
  } finally {
    window.clearTimeout(timeout)
  }
}

function buildLaunchContextUrl(
  base: string | undefined,
  context: DiscordActivityContext
): URL | null {
  const withContext = (url: URL): URL => {
    if (context.guildId) url.searchParams.set('guildId', context.guildId)
    if (context.channelId) url.searchParams.set('channelId', context.channelId)
    return url
  }
  const trimmed = base?.trim()
  if (!trimmed) {
    return import.meta.env.PROD
      ? withContext(new URL('/api/activity/launch-context', window.location.origin))
      : null
  }

  try {
    if (trimmed.startsWith('/')) {
      const href = trimmed.endsWith('/') ? trimmed : `${trimmed}/`
      return withContext(
        new URL('activity/launch-context', new URL(href, window.location.origin))
      )
    }

    const baseUrl = new URL(trimmed)
    const localHost =
      baseUrl.hostname === 'localhost' ||
      baseUrl.hostname === '127.0.0.1' ||
      baseUrl.hostname === '[::1]'
    if (baseUrl.protocol !== 'https:' && !localHost) return null
    const href = trimmed.endsWith('/') ? trimmed : `${trimmed}/`
    return withContext(new URL('activity/launch-context', href))
  } catch {
    return null
  }
}

function normalizeLaunchContext(input: unknown): LoadedActivityLaunchContext | null {
  const root = record(input)
  if (!root || root.ok !== true || root.version !== 1) return null
  const surfaceKey =
    typeof root.surfaceKey === 'string' ? root.surfaceKey.trim() : ''
  const surface = surfaceByKey(surfaceKey)
  if (!surface) return null

  return {
    surface,
    sourceCommand: nullableText(root.sourceCommand, 80),
    sourceSubcommand: nullableText(root.sourceSubcommand, 80),
    fallback: Boolean(root.fallback)
  }
}

function record(input: unknown): Record<string, unknown> | null {
  return input && typeof input === 'object' && !Array.isArray(input)
    ? (input as Record<string, unknown>)
    : null
}

function nullableText(input: unknown, maxLength: number): string | null {
  if (typeof input !== 'string') return null
  const clean = input.replace(/\s+/g, ' ').trim().slice(0, maxLength)
  return clean || null
}
